// Sdílený stav klienta UNO (poslední pohled, kurzor v ruce, výběr barvy) a české texty.
import { COLOR_NAME, COLORS, cardName, isPlayable, type Card, type Color, type UnoView } from "../../../games/uno/meta.ts";

export interface Model {
  me: string;
  view: UnoView | null;
  /** Index vybrané karty v ruce. */
  cursor: number;
  /** Vybírám barvu pro černou kartu. */
  picking: { cardId: number; index: number } | null;
  sendAction(action: unknown): void;
}

export { cardName, COLOR_NAME, COLORS };

export function playable(view: UnoView, card: Card): boolean {
  if (view.turn === undefined || view.over) return false;
  if (view.drawnCardId !== null && card.id !== view.drawnCardId) return false;
  return isPlayable(card, view.hand, view.top, view.color, view.pendingDraw, view.stacking);
}

export function playableCount(view: UnoView): number {
  return view.hand.filter((c) => playable(view, c)).length;
}

export function playerName(view: UnoView, id: string, me: string): string {
  if (id === me) return "ty";
  return view.players.find((p) => p.id === id)?.name ?? "soupeř";
}

export function count(n: number, one: string, few: string, many: string): string {
  return `${n} ${n === 1 ? one : n >= 2 && n <= 4 ? few : many}`;
}

export function cards(n: number): string {
  return count(n, "karta", "karty", "karet");
}

/** „červená sedmička“, u černé karty i zvolená barva. */
export function topText(view: UnoView): string {
  const name = cardName(view.top);
  return view.top.color ? name : `${name}, barva ${COLOR_NAME[view.color]}`;
}

export function turnText(view: UnoView, me: string): string {
  return view.turn === me ? "Jsi na tahu." : `Na tahu je ${playerName(view, view.turn, me)}.`;
}

export function countsText(view: UnoView, me: string): string {
  return view.players.map((p) => `${p.id === me ? "ty máš" : `${p.name} má`} ${cards(p.cards)}`).join(", ") + ".";
}

export function resultText(view: UnoView, me: string): string {
  const w = view.winners[0];
  return w === me ? "Konec hry. Vítěz: ty!" : `Konec hry. Vítěz: ${playerName(view, w, me)}.`;
}

/** Co mám teď udělat (krátká nápověda k tahu). */
export function hintText(view: UnoView, me: string): string {
  if (view.over || view.turn !== me) return "";
  if (view.drawnCardId !== null) return "Líznutou kartu můžeš zahrát Enterem, nebo ji nech klávesou P a tah předáš.";
  if (view.pendingDraw > 0) {
    const n = playableCount(view);
    return n
      ? `Na tebe čeká ${cards(view.pendingDraw)} k líznutí. Můžeš odpovědět svou plus dva, nebo L pro líznutí.`
      : `Musíš lízat ${cards(view.pendingDraw)}: stiskni L.`;
  }
  const n = playableCount(view);
  return n ? `Hratelné: ${n}.` : "Nemáš co zahrát, lízni klávesou L.";
}

export function cardColorOrBlack(card: Card): Color | "k" {
  return card.color ?? "k";
}

/** Panoráma podle pozice v ruce: první karta vlevo, poslední vpravo. */
export function panFor(index: number, total: number): number {
  return total > 1 ? ((index / (total - 1)) * 2 - 1) * 0.8 : 0;
}

/** Indexy hráčů v pořadí tahu, od toho, kdo je právě na tahu (podle směru hry). */
export function turnOrder(view: UnoView): number[] {
  const n = view.players.length;
  const start = Math.max(0, view.players.findIndex((p) => p.id === view.turn));
  return Array.from({ length: n }, (_, k) => (((start + k * view.direction) % n) + n) % n);
}

/** „Na tahu Jirka, 3 karty; pak ty, 5 karet; pak Robot, 2 karty. Směr po směru.“ */
export function orderText(view: UnoView, me: string): string {
  const parts = turnOrder(view).map((i, k) => {
    const p = view.players[i];
    const who = p.id === me ? "ty" : p.name;
    return `${k === 0 ? "Na tahu" : "pak"} ${who}, ${cards(p.cards)}`;
  });
  const dir = view.players.length > 2 ? (view.direction === 1 ? " Směr po směru." : " Směr obrácený.") : "";
  return `${parts.join("; ")}.${dir}`;
}
